import type { MeResponse } from '../../shared/api-types.js';
import type { IconName } from '../ui/Icon.js';

export interface Destination {
  label: string;
  to: string;
  icon: IconName;
  /** Match exactly (the root), not by prefix. */
  end?: boolean;
  /**
   * Offered only while the person holds this capability (grant and floor, as /api/me resolves it).
   * Selection, never enforcement: the routes answer for themselves (permission-model P8).
   */
  requires?: keyof MeResponse['capabilities'];
  /** Offered only while this integration is not `off` on the installation (its floor, as /api/me reports it). Selection, as above. */
  integration?: keyof MeResponse['integrations'];
}

/**
 * The primary destinations, identical on the desktop rail and the mobile tab bar, in this order
 * (spec §13; 02-patterns §1.4). Built once here and shared by every view. The Projects destination
 * (owner ruling 8 of 2026-10-02, ADR 0017) is offered wherever the `PROJECTS_MODE` floor is on (ruling 9:
 * no grant decides who files; the floor binds the superuser too); the Assistant to those it serves (owner
 * ruling A9).
 *
 * Why Executive fifth (ADR 0019; the owner's ruling on design §5.16 of 2026-10-06/07, "Executive fifth"): the
 * seventh destination sits after Projects and before the Assistant, offered to whoever holds
 * `executive_intelligence` under its floor — so the executives' own destination is never the one folded away. Past
 * six, the bar folds by the one rule below (`foldDestinations`): an executive who also holds the Assistant sees five
 * destinations and More holding the Assistant and Connections; an executive without it is offered six and sees them
 * all; every non-executive's bar is exactly what it was, in every slot.
 */
export const DESTINATIONS: readonly Destination[] = [
  { label: 'Overview', to: '/', icon: 'home', end: true },
  { label: 'To-dos', to: '/todos', icon: 'check-square' },
  { label: 'Meetings', to: '/meetings', icon: 'video' },
  { label: 'Projects', to: '/projects', icon: 'folder', integration: 'projects' },
  { label: 'Executive', to: '/executive', icon: 'pulse', requires: 'executiveIntelligence', integration: 'executive' },
  { label: 'Assistant', to: '/assistant', icon: 'sparkles', requires: 'aiSummaries', integration: 'assistant' },
  { label: 'Connections', to: '/connections', icon: 'plug' },
];

/** The destinations this person is offered, in order — the same set on the rail and the tab bar. */
export function destinationsFor(me: Pick<MeResponse, 'capabilities' | 'integrations'>): Destination[] {
  return DESTINATIONS.filter(
    (destination) =>
      (destination.requires === undefined || me.capabilities[destination.requires]) &&
      (destination.integration === undefined || me.integrations[destination.integration] !== 'off'),
  );
}

/**
 * The tab bar's ceiling (02-patterns §1.4: "three to six items. More MUST fold into a 'More' destination rather than
 * shrinking below the 44px target").
 */
export const BAR_MAX = 6;

export interface FoldedDestinations {
  /** The destinations drawn on the bar, in order. */
  shown: Destination[];
  /** The destinations under More, in order — empty when nothing folds, and then no More item is drawn. */
  more: Destination[];
}

/**
 * The one fold, on the rail and the tab bar alike (ADR 0019; design §2.13): the offered list whole while it holds six or
 * fewer, otherwise the first five and More holding the rest in order. Why the rail folds too, though it could carry seven
 * (02-patterns §1.4: "at most eight"): the destinations "MUST be identical on both, in the same order", so the two
 * surfaces stay structurally one — the owner's chosen option.
 */
export function foldDestinations(offered: readonly Destination[]): FoldedDestinations {
  if (offered.length <= BAR_MAX) return { shown: [...offered], more: [] };
  return { shown: offered.slice(0, BAR_MAX - 1), more: offered.slice(BAR_MAX - 1) };
}

/** Whether a path is a destination's own (its page or one beneath it), as the router's NavLink matches it. */
export function isAtDestination(destination: Pick<Destination, 'to' | 'end'>, pathname: string): boolean {
  if (destination.end) return pathname === destination.to;
  return pathname === destination.to || pathname.startsWith(`${destination.to}/`);
}

/** The folded destination the reader is on, if any — More then carries the active cue (02-patterns §1.4: two signals). */
export function currentFolded(more: readonly Destination[], pathname: string): Destination | null {
  return more.find((destination) => isAtDestination(destination, pathname)) ?? null;
}
